import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const StarRow = ({ rating, size = 'w-4 h-4' }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`${size} ${n <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-600'}`}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const CourseReviewsSection = ({ courseId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/courses/${courseId}/reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [courseId]);

  if (loading) return null;
  if (!data) return null;

  const summary = data.summary || { avg: 0, count: 0, distribution: {} };
  const reviews = data.reviews || [];
  const visible = showAll ? reviews : reviews.slice(0, 4);
  const total = summary.count || 0;
  const dist = summary.distribution || {};

  // Hide section entirely if no approved reviews yet (avoid empty-state clutter)
  if (total === 0) return null;

  return (
    <Card className="bg-white/5 border-golden/20" data-testid="course-reviews-section">
      <CardContent className="p-6 space-y-5">
        <div>
          <h2 className="text-xl font-bold text-white mb-4">Student Reviews</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
            {/* Aggregate */}
            <div className="text-center md:text-left">
              <div className="text-5xl font-bold text-golden mb-1" data-testid="course-rating-avg">{summary.avg.toFixed(1)}</div>
              <StarRow rating={summary.avg} size="w-5 h-5" />
              <p className="text-gray-400 text-xs mt-2">{total} review{total === 1 ? '' : 's'}</p>
            </div>

            {/* Distribution bars */}
            <div className="md:col-span-2 space-y-1.5">
              {[5, 4, 3, 2, 1].map((star) => {
                const c = Number(dist[star] || 0);
                const pct = total ? Math.round((c / total) * 100) : 0;
                return (
                  <div key={star} className="flex items-center gap-2 text-xs">
                    <span className="text-gray-400 w-3">{star}</span>
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-400 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-gray-500 w-8 text-right">{c}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Individual reviews */}
        <div className="space-y-4">
          {visible.map((r) => (
            <div key={r.id} className="border-t border-white/5 pt-4" data-testid={`course-review-${r.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="text-sm font-semibold text-white">{r.user_name}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {r.approved_at ? new Date(r.approved_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : ''}
                  </div>
                </div>
                <StarRow rating={r.rating} />
              </div>
              <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">{r.content}</p>
              {r.is_featured && (
                <span className="inline-block mt-2 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded">
                  Featured
                </span>
              )}
            </div>
          ))}
        </div>

        {reviews.length > 4 && (
          <button
            onClick={() => setShowAll((s) => !s)}
            className="text-golden text-sm hover:underline"
            data-testid="course-reviews-toggle"
          >
            {showAll ? 'Show less' : `Show all ${reviews.length} reviews`}
          </button>
        )}
      </CardContent>
    </Card>
  );
};

export default CourseReviewsSection;
